import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { logger } from '@/lib/logger';

const emailSchema = z.object({
  email: z.string().trim().email({ message: 'Please enter a valid email address' }).max(255, { message: 'Email must be less than 255 characters' })
});

// The footer's signup. A full-width card variant existed but nothing rendered
// it; it was removed in the 2026-10-13 site review (F7).
export const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [touched, setTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const statusRef = useRef<HTMLParagraphElement>(null);

  // An empty field only earns a message once Subscribe was pressed, so
  // tabbing through the footer never nags; typing clears it.
  const fieldError =
    email.trim().length === 0
      ? submitAttempted ? 'Enter your email address.' : ''
      : touched && !emailSchema.safeParse({ email }).success
        ? 'Please enter a valid email address.'
        : '';

  // The form is swapped for the confirmation, which would drop keyboard focus
  // to the page; hand it to the confirmation instead.
  useEffect(() => {
    if (isSubscribed) statusRef.current?.focus();
  }, [isSubscribed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitAttempted(true);
    const validation = emailSchema.safeParse({ email });
    if (!validation.success) {
      setTouched(true);
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('subscribe-newsletter', {
        body: { email: validation.data.email.toLowerCase() }
      });

      if (error) {
        const status = error instanceof FunctionsHttpError ? (error.context as Response | undefined)?.status : undefined;
        if (status === 429) {
          toast.error('Too many requests', {
            description: 'Please wait a moment before trying again.',
          });
        } else {
          throw error;
        }
      } else if (data?.success) {
        setIsSubscribed(true);
        setEmail('');
        setTouched(false);
        setSubmitAttempted(false);
        toast.success('Successfully subscribed', {
          description: 'If this is a new address, a welcome email is on its way.',
        });
      } else if (data?.error) {
        throw new Error(data.error);
      }
    } catch (error) {
      logger.error('Error subscribing to newsletter:', error);
      toast.error('Subscription failed', {
        description: 'There was an error subscribing to the newsletter. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return isSubscribed ? (
    <p ref={statusRef} tabIndex={-1} role="status" aria-live="polite" className="flex h-9 items-center gap-2 text-sm text-foreground focus:outline-none">
      <CheckCircle2 className="h-4 w-4 text-success-text" />
      You're on the list.
    </p>
  ) : (
    <form onSubmit={handleSubmit} noValidate className="[&>*+*]:mt-2 [&>label+div]:mt-0">
      <label htmlFor="footer-newsletter-email" className="sr-only">Email address</label>
      <div className="space-y-1.5">
        <Input
          id="footer-newsletter-email"
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setTouched(true)}
          aria-invalid={fieldError ? true : undefined}
          aria-describedby={fieldError ? 'footer-newsletter-email-error' : undefined}
          disabled={isLoading}
          required
          autoComplete="email"
          spellCheck={false}
          className="h-9 w-full max-w-[11rem] border-muted-foreground/70 bg-background px-3 text-sm placeholder:text-muted-foreground dark:border-muted-foreground/50 focus-ring-subtle"
        />
        <Button type="submit" size="sm" disabled={isLoading || Boolean(fieldError)} className="h-7 min-w-[4.5rem] rounded-md px-2.5 text-xs">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </div>
      {fieldError && (
        <p id="footer-newsletter-email-error" role="alert" className="text-xs text-destructive-text">{fieldError}</p>
      )}
    </form>
  );
};
